<template>
    <!-- Main container -->
    <div class="register-container">
        <AnimatedBackground/>

        <!-- Back to Home Link -->
        <router-link to="/" class="back-link">
            <ArrowLeft :size="20" />
            <span>Back to Home</span>
        </router-link>

        <!-- Card wrapper -->
        <div class="register-card">
            <!-- Header -->
            <h1>Curie</h1>
            <p class="subtitle">Create your account</p>
            
            <!-- Register Form -->
            <form @submit.prevent="handleRegister">
                <!-- Full Name Field -->
                <div class="form-group">
                    <label for="fullName">Full Name</label>
                    <input
                        id="fullName"
                        v-model="fullName"
                        type="text"
                        placeholder="John Doe"
                        required
                        autocomplete="name"
                    />
                </div>

                <!-- School Field -->
                <div class="form-group">
                    <label for="school">School</label>
                    <v-select
                        id="school"
                        v-model="school"
                        :options="nswSchools"
                        placeholder="Search for your school..."
                        :clearable="false"
                    />
                </div>

                <!-- Email Field -->
                <div class="form-group">
                    <label for="email">Email</label>
                    <input
                        id="email"
                        v-model="email"
                        type="email"
                        placeholder="your.email@example.com"
                        required
                        autocomplete="email"
                    />
                </div>
                
                <!-- Password Field -->
                <div class="form-group">
                    <label for="password">Password</label>
                    <input
                        id="password"
                        v-model="password"
                        type="password"
                        placeholder="Enter your password"
                        required
                        autocomplete="new-password"
                    />
                </div>

                <!-- Confirm Password Field -->
                <div class="form-group">
                    <label for="confirmPassword">Confirm Password</label>
                    <input
                        id="confirmPassword"
                        v-model="confirmPassword"
                        type="password"
                        placeholder="Confirm your password"
                        required
                        autocomplete="new-password"
                    />
                </div>
                
                <!-- Error Message -->
                <div v-if="errorMessage" class="error-message">
                    {{ errorMessage }}
                </div>
                
                <!-- Submit Button -->
                <button 
                    type="submit" 
                    class="submit-btn"
                    :disabled="loading"
                >
                    {{ loading ? 'Creating Account...' : 'Register' }}
                </button>
            </form>
            
            <!-- Login Link -->
            <p class="login-link">
                Already have an account? 
                <router-link to="/login">Login here</router-link>
            </p>
        </div>
    </div>
</template>

<script>
// Import Vue functions
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import AnimatedBackground from '@/components/AnimatedBackground.vue';
import { ArrowLeft } from 'lucide-vue-next';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { nswSchools } from '@/data/nswSchools';

export default {
    name: 'Register',
    components: {
        AnimatedBackground,
        ArrowLeft,
        vSelect,
    },
    setup() {
        // ==================== SETUP ====================
        
        // Get router (for navigation after registration)
        const router = useRouter();
        
        // Get auth store (for register function)
        const authStore = useAuthStore();
        
        // ==================== STATE (Reactive Data) ====================
        
        const fullName = ref('');
        const school = ref('');
        const email = ref('');
        const password = ref('');
        const confirmPassword = ref('');
        const loading = ref(false);
        const errorMessage = ref('');
        
        // ==================== FUNCTIONS ====================
        
        /**
         * Handle register form submission
         */
        const handleRegister = async () => {
            // Clear any previous errors
            errorMessage.value = '';

            // Validate passwords match
            if (password.value !== confirmPassword.value) {
                errorMessage.value = 'Passwords do not match';
                return;
            }

            // Validate password complexity (must match backend requirements)
            const pwErrors = [];
            if (password.value.length < 10) pwErrors.push('at least 10 characters');
            if (!/[A-Z]/.test(password.value)) pwErrors.push('an uppercase letter');
            if (!/[a-z]/.test(password.value)) pwErrors.push('a lowercase letter');
            if (!/[0-9]/.test(password.value)) pwErrors.push('a digit');
            if (pwErrors.length) {
                errorMessage.value = `Password must contain ${pwErrors.join(', ')}`;
                return;
            }
            
            // Set loading state
            loading.value = true;
            
            try {
                // Call the register function from auth store
                await authStore.register(fullName.value, email.value, password.value, school.value);
                
                // If successful, redirect to practice page
                router.push('/practice');
                
            } catch (error) {
                // If registration fails, show error message
                errorMessage.value = error.message || 'Registration failed. Please try again.';
                
            } finally {
                // Always turn off loading state
                loading.value = false;
            }
        };
        
        // ==================== RETURN ====================
        
        return {
            fullName,
            school,
            email,
            password,
            confirmPassword,
            loading,
            errorMessage,
            handleRegister,
            nswSchools,
        };
    }
};
</script>

<style scoped>
/* Font and global styling to match practice page */
* {
    font-family: "Space Grotesk", -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 400;
    letter-spacing: 1px;
}

/* Main container - centers everything with matching gradient */
.register-container {
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    background: var(--bg-primary);
}

/* Card that holds the form - matching practice card style */
.register-card {
    background: rgba(var(--bg-primary-rgb), 1);
    padding: 40px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    width: 100%;
    max-width: 450px;
    z-index: 1;
}

/* Header */
h1 {
    margin: 0 0 10px 0;
    font-size: 32px;
    color: var(--text-secondary);
    text-align: center;
    font-weight: 600;
}

.subtitle {
    margin: 0 0 30px 0;
    color: rgba(var(--text-primary-rgb), 0.7);
    text-align: center;
    font-size: 15px;
}

/* Form Groups */
.form-group {
    margin-bottom: 20px;
}

label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: var(--text-secondary);
    font-size: 14px;
}

input {
    width: 100%;
    padding: 14px 16px;
    background: rgba(255, 246, 227, 0.05);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    font-size: 15px;
    color: var(--text-primary);
    transition: all 0.3s ease;
    box-sizing: border-box;
}

input:hover {
    border: 1px solid rgba(var(--accent-rgb), 1);
}

input::placeholder {
    color: rgba(var(--text-primary-rgb), 0.4);
}

input:focus {
    outline: none;
    border-color: var(--accent);
    background-color: rgba(255, 246, 227, 0.08);
}

/* Vue Select Styling */
:deep(.v-select) {
    --vs-controls-color: var(--text-primary);
    --vs-border-color: rgba(var(--accent-rgb), 0.4);
    --vs-dropdown-bg: var(--bg-primary);
    --vs-dropdown-option-color: var(--text-primary);
    --vs-selected-color: var(--text-primary);
    --vs-search-input-color: var(--text-primary);
    --vs-dropdown-option--active-bg: rgba(var(--accent-rgb), 0.2);
    --vs-dropdown-option--active-color: var(--text-primary);
}

:deep(.vs__dropdown-toggle) {
    background: rgba(255, 246, 227, 0.05);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    padding: 10px 12px;
    min-height: 48px;
    transition: border-color 0.3s ease;
}

:deep(.vs__dropdown-toggle:hover) {
    border-color: rgba(var(--accent-rgb), 1);
}

:deep(.vs__search),
:deep(.vs__search:focus) {
    color: var(--text-primary);
    font-size: 15px;
    margin: 0;
    padding: 0;
}

:deep(.vs__search::placeholder) {
    color: rgba(var(--text-primary-rgb), 0.4);
}

:deep(.vs__selected) {
    color: var(--text-primary);
    font-size: 15px;
    margin: 0;
    padding: 0;
}

/* Hide search input when a value is selected */
:deep(.vs--single.vs--open .vs__selected) {
    opacity: 0.4;
}

:deep(.vs__selected + .vs__search) {
    position: absolute;
    width: 100%;
    opacity: 0;
}

:deep(.vs--open .vs__selected + .vs__search) {
    position: static;
    width: auto;
    opacity: 1;
}

:deep(.vs__dropdown-menu) {
    background: var(--bg-primary);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    max-height: 250px;
    scrollbar-width: thin;
    scrollbar-color: rgba(var(--accent-rgb), 0.5) transparent;
}

:deep(.vs__dropdown-menu::-webkit-scrollbar) {
    width: 8px;
}

:deep(.vs__dropdown-menu::-webkit-scrollbar-track) {
    background: transparent;
}

:deep(.vs__dropdown-menu::-webkit-scrollbar-thumb) {
    background: rgba(var(--accent-rgb), 0.5);
    border-radius: 4px;
}

:deep(.vs__dropdown-menu::-webkit-scrollbar-thumb:hover) {
    background: rgba(var(--accent-rgb), 0.7);
}

:deep(.vs__dropdown-option) {
    padding: 12px 16px;
    font-size: 14px;
}

:deep(.vs__dropdown-option--highlight) {
    background: rgba(var(--accent-rgb), 0.2);
    color: var(--text-primary);
}

:deep(.vs__clear),
:deep(.vs__open-indicator) {
    fill: rgba(var(--text-primary-rgb), 0.5);
}

:deep(.vs--open .vs__dropdown-toggle) {
    border-color: var(--accent);
    background-color: rgba(255, 246, 227, 0.08);
}

/* Error Message */
.error-message {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 20px;
    font-size: 14px;
}

/* Submit Button - matching practice page buttons */
.submit-btn {
    width: 100%;
    padding: 16px;
    background: var(--text-primary);
    color: var(--bg-primary);
    border: none;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    margin-top: 10px;
}

.submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Login Link */
.login-link {
    text-align: center;
    margin-top: 24px;
    color: rgba(var(--text-primary-rgb), 0.7);
    font-size: 14px;
}

.login-link a {
    color: var(--text-secondary);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
}

.login-link a:hover {
    color: var(--text-primary);
    text-decoration: underline;
}

/* Back to Home Link */
.back-link {
    position: absolute;
    top: 20px;
    left: 20px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--accent);
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    transition: transform 0.2s ease;
    z-index: 10;
}

.back-link:hover {
    transform: translateX(-4px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .register-card {
        padding: 30px 24px;
        max-width: 100%;
    }

    h1 {
        font-size: 28px;
    }
}
</style>